import styled from '@emotion/styled';
import { mediaQuery, Spacings } from '@styles/index';
import Typography from 'aquilla/core/atoms/Typography';
import React from 'react';
import { useIsMobile } from '@stores/AppCommonData';
import Image from '@base/Image';
import { useRouter } from 'next/router';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsUserSubscribedToStoreBundle } from '@api/selectors/subscription';
import SkuPricingDetails from './SkuPricingDetails';

const Container = styled.div`
  margin-bottom: ${Spacings.SPACING_10B};
  ${({ $isSticky }) => {
    if ($isSticky) {
      return `
        border: 1px solid var(--color-divider);
        border-radius: ${Spacings.SPACING_2B};
        padding: ${Spacings.SPACING_4B};
        height: max-content;
        position: sticky;
        top: 168px;
      `;
    }
  }}

  ${({ $isHeaderShown }) => {
    if ($isHeaderShown) {
      return `
        opacity: 1;
        user-select: auto;
      `;
    }
    return `
      opacity: 0;
      z-index: -1;
      user-select: none;
    `;
  }} 
  transition: opacity 0.2s ease-in-out;

  ${mediaQuery.uptoMobile} {
    width: 100%;
    margin-bottom: ${Spacings.SPACING_5B};
  }
`;

const Title = styled(Typography)`
  margin-top: ${Spacings.SPACING_3B};
  max-width: 512px;

  ${mediaQuery.uptoMobile} {
    width: 100%;
    margin-top: ${Spacings.SPACING_0B};
  }
`;

const FlexRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const FlexRowWithMargin = styled(FlexRow)`
  margin-top: ${Spacings.SPACING_4B};
  ${mediaQuery.uptoMobile} {
    margin-top: ${Spacings.SPACING_2B};
  }
`;

const Wrapper = styled.div`
  margin-top: ${Spacings.SPACING_4B};
`;

const ValueIcon = styled.div`
  margin-right: 8px;
  height: 24px;
  width: 24px;
`;

const NotesDetails = ({ isSticky, isHeaderShown, data }) => {
  const { title, goal, valueProps, skuDetails } = data || {};

  const isMobile = useIsMobile();

  const {
    query: { noteUID }
  } = useRouter();

  const hasActiveNotesSubscription = useMyInfo(
    getIsUserSubscribedToStoreBundle(noteUID, '600')
  );

  const renderActions = () => {
    return (
      <SkuPricingDetails
        isSticky={isSticky}
        goalUid={goal?.uid}
        skuDetails={skuDetails}
      />
    );
  };
  return (
    <Container $isSticky={isSticky} $isHeaderShown={isHeaderShown}>
      <Title as="h1" variant={isSticky || isMobile ? 'h4' : 'h2'}>
        {title}
      </Title>
      <Wrapper $isSticky={isSticky}>
        {valueProps?.map((valueProp, index) => {
          return (
            <FlexRowWithMargin key={String(index)}>
              <ValueIcon>
                <Image
                  src={isSticky ? valueProp.scrollIcon : valueProp.icon}
                  alt="valueProp-icon"
                  width={24}
                  height={24}
                />
              </ValueIcon>
              <Typography variant="p3"> {valueProp.label} </Typography>
            </FlexRowWithMargin>
          );
        })}
      </Wrapper>
      {!hasActiveNotesSubscription ? renderActions() : null}
    </Container>
  );
};

export default React.memo(NotesDetails);
